import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { object, string, ref } from 'yup';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { checkSetPasswordTokenStatusAPI, setPasswordFromTokenAPI } from '../constants';
import bgImage from '../assets/images/bgImg1.png';
import bgBase from '../assets/images/bgBase.png';

const passwordErrorMsg = "Password must contain at least:\nOne Uppercase letter\nOne Lowercase letter\nOne Number\nOne Special character";

export default function SetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [isConfirmPasswordFocused, setIsConfirmPasswordFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [tokenStatus, setTokenStatus] = useState(null); // can_set | already_set | invalid | expired | not_found

  const tokenFromUrl = searchParams.get('token');
  const emailFromUrl = searchParams.get('email');
  const workspaceFromUrl = searchParams.get('workspace');
  const projectFromUrl = searchParams.get('project');

  useEffect(() => {
    let cancelled = false;

    const checkStatus = async () => {
      if (!tokenFromUrl) {
        setTokenStatus('invalid');
        setStatusLoading(false);
        toast.error('Invalid or missing invitation token', {
          className: 'bg-secondary-500 text-white',
        });
        return;
      }

      setStatusLoading(true);
      try {
        const response = await checkSetPasswordTokenStatusAPI(tokenFromUrl);
        if (cancelled) return;
        setTokenStatus(response?.status || 'invalid');
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.data?.status;
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Invalid or expired token.';
        if (status === 'already_set') {
          setTokenStatus('already_set');
        } else if (status === 'expired' || status === 'not_found' || status === 'invalid') {
          setTokenStatus(status);
          toast.error(message, { className: 'bg-secondary-500 text-white' });
        } else {
          setTokenStatus('invalid');
          toast.error(message, { className: 'bg-secondary-500 text-white' });
        }
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    };

    checkStatus();
    return () => {
      cancelled = true;
    };
  }, [tokenFromUrl]);

  const validationSchema = object({
    password: string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(
        /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+}{"':;?/>.<,]).*$/,
        passwordErrorMsg,
      ),
    confirmPassword: string()
      .required('Confirm password is required')
      .oneOf([ref('password')], 'Passwords must match'),
  });

  const formik = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema,
    onSubmit: async (values) => {
      if (!tokenFromUrl) {
        toast.error('Invalid or missing invitation token', {
          className: 'bg-secondary-500 text-white',
        });
        return;
      }

      try {
        setLoading(true);
        const response = await setPasswordFromTokenAPI(
          tokenFromUrl,
          values.password,
          values.confirmPassword,
        );

        if (response?.success || response?.status === 201) {
          toast.success('Password set successfully! Redirecting to login...', {
            className: 'bg-green-500 text-white',
          });

          setTimeout(() => {
            navigate('/');
          }, 1500);
        } else if (response?.status === 'already_set') {
          setTokenStatus('already_set');
          toast.info(response?.message || 'Password already set. Please login to your account.', {
            className: 'bg-secondary-500 text-white',
          });
        } else {
          toast.error(response?.message || response?.error || 'Failed to set password', {
            className: 'bg-secondary-500 text-white',
          });
        }
      } catch (err) {
        console.error('Error setting password:', err);
        const data = err?.response?.data;
        if (data?.status === 'already_set') {
          setTokenStatus('already_set');
          toast.info(data?.message || data?.error || 'Password already set. Please login to your account.', {
            className: 'bg-secondary-500 text-white',
          });
        } else {
          const errorMessage = data?.message || data?.error || err?.message || 'Failed to set password';
          toast.error(errorMessage, {
            className: 'bg-secondary-500 text-white',
          });
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const renderAlreadySet = () => (
    <div className="mt-6">
      <p className="text-base text-gray-200 mb-6">
        Password already set. Please login to your account.
      </p>
      <button
        type="button"
        onClick={() => navigate('/')}
        className="w-full p-2 rounded-md bg-blue-500 text-white font-semibold hover:bg-blue-600 transition duration-300"
      >
        Go to Login
      </button>
      <div className="mt-4 text-center">
        <Link to="/forgotPassword">
          <span className="text-blue-500 font-semibold opacity-75 cursor-pointer text-sm">
            Forgot Password ?
          </span>
        </Link>
      </div>
    </div>
  );

  const renderInvalid = () => (
    <div className="mt-6">
      <p className="text-base text-gray-200 mb-6">
        This invitation link is invalid or has already been used.
      </p>
      <button
        type="button"
        onClick={() => navigate('/')}
        className="w-full p-2 rounded-md bg-blue-500 text-white font-semibold hover:bg-blue-600 transition duration-300"
      >
        Go to Login
      </button>
    </div>
  );

  return (
    <div className="bg-secondary-600 flex flex-row w-full overflow-hidden">
      <div className="relative md:w-[55%] lg:w-[62%]">
        <div className="relative h-screen flex">
          <img src={bgBase} className="fixed bottom-0 left-0 w-full h-1/3" alt="Surface-image" />
          <img
            src={bgImage}
            className="relative bg-no-repeat bg-fixed bg-center bg-cover w-2/3 ml-[25%] my-auto"
            alt="Background-image"
          />
        </div>
      </div>
      <div className="bg-secondary-500 w-[45%] lg:w-[38%] h-screen relative flex">
        <div className="relative my-auto w-4/5 ml-[10%]">
          <div className="flex flex-col relative w-full mb-4">
            <h1 className="text-2xl font-semibold text-gray-300 flex items-center">
              {tokenStatus === 'already_set' ? 'Password Already Set' : 'Set Your Password'}
            </h1>
            <h1 className="text-base text-gray-400">
              {tokenStatus === 'already_set'
                ? 'You can sign in with your existing password'
                : 'Create a secure password for your account'}
            </h1>
          </div>
          <div className="relative">
            <div className="sm:col-span-4 w-full">
              <label className="block text-sm mb-2 font-medium leading-6 text-gray-300">
                Email
              </label>
              <div>
                <p className="text-gray-200 w-full py-1.5 pl-1 sm:text-sm sm:leading-6">
                  {emailFromUrl || 'N/A'}
                </p>
              </div>
            </div>

            {workspaceFromUrl ? (
              <div className="sm:col-span-4 w-full mt-6">
                <label className="block text-sm mb-2 font-medium leading-6 text-gray-300">
                  Workspace
                </label>
                <div>
                  <p className="text-gray-200 w-full py-1.5 pl-1 sm:text-sm sm:leading-6">
                    {workspaceFromUrl}
                  </p>
                </div>
              </div>
            ) : null}

            {projectFromUrl ? (
              <div className="sm:col-span-4 w-full mt-6">
                <label className="block text-sm mb-2 font-medium leading-6 text-gray-300">
                  Project
                </label>
                <div>
                  <p className="text-gray-200 w-full py-1.5 pl-1 sm:text-sm sm:leading-6">
                    {projectFromUrl}
                  </p>
                </div>
              </div>
            ) : null}

            {statusLoading ? (
              <p className="mt-8 text-sm text-gray-400">Checking invitation status...</p>
            ) : tokenStatus === 'already_set' ? (
              renderAlreadySet()
            ) : tokenStatus === 'can_set' ? (
              <form onSubmit={formik.handleSubmit}>
                <div className="sm:col-span-4 w-full mt-6">
                  <label
                    htmlFor="password"
                    className={`block text-sm mb-2 font-medium leading-6 ${
                      formik.touched.password && formik.errors.password
                        ? 'text-red-500'
                        : isPasswordFocused
                          ? 'text-blue-500'
                          : 'text-gray-300'
                    }`}
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      id="password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={() => setIsPasswordFocused(false)}
                      onFocus={() => setIsPasswordFocused(true)}
                      className={`text-gray-200 w-full border-gray-500 outline-none hover:border-gray-300 rounded-md bg-transparent py-1.5 pl-1 placeholder:text-gray-400 sm:text-sm sm:leading-6 ${
                        formik.touched.password && formik.errors.password
                          ? 'border-none outline-1 outline-red-500'
                          : ''
                      }`}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPassword((prev) => !prev);
                      }}
                      className="absolute mx-auto mt-5 text-blue-500 opacity-75 right-[1%] pr-3 bg-transparent transform -translate-y-1/2 focus:outline-none"
                      tabIndex="-1"
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                    </button>
                  </div>
                  {formik.touched.password && formik.errors.password ? (
                    <div className="text-red-500 text-sm mt-1">{formik.errors.password}</div>
                  ) : null}
                </div>

                <div className="sm:col-span-4 w-full mt-6">
                  <label
                    htmlFor="confirmPassword"
                    className={`block text-sm mb-2 font-medium leading-6 ${
                      formik.touched.confirmPassword && formik.errors.confirmPassword
                        ? 'text-red-500'
                        : isConfirmPasswordFocused
                          ? 'text-blue-500'
                          : 'text-gray-300'
                    }`}
                  >
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      id="confirmPassword"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={() => setIsConfirmPasswordFocused(false)}
                      onFocus={() => setIsConfirmPasswordFocused(true)}
                      className={`text-gray-200 w-full border-gray-500 outline-none hover:border-gray-300 rounded-md bg-transparent py-1.5 pl-1 placeholder:text-gray-400 sm:text-sm sm:leading-6 ${
                        formik.touched.confirmPassword && formik.errors.confirmPassword
                          ? 'border-none outline-1 outline-red-500'
                          : ''
                      }`}
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowConfirmPassword((prev) => !prev);
                      }}
                      className="absolute mx-auto mt-5 text-blue-500 opacity-75 right-[1%] pr-3 bg-transparent transform -translate-y-1/2 focus:outline-none"
                      tabIndex="-1"
                      aria-label="Toggle confirm password visibility"
                    >
                      {showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                    </button>
                  </div>
                  {formik.touched.confirmPassword && formik.errors.confirmPassword ? (
                    <div className="text-red-500 text-sm mt-1">{formik.errors.confirmPassword}</div>
                  ) : null}
                </div>

                <button
                  type="submit"
                  className={`w-full p-2 rounded-md bg-blue-500 text-white font-semibold hover:bg-blue-600 transition duration-300 mt-6 ${
                    loading || !formik.isValid ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={loading || !formik.isValid}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="animate-spin h-5 w-5 text-white mr-2"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 4.75v1.5M6.343 6.343l1.06 1.06M4.75 12h1.5M6.343 17.657l1.06-1.06M12 18.25v1.5M17.657 17.657l-1.06-1.06M18.25 12h-1.5M17.657 6.343l-1.06 1.06"
                        />
                      </svg>
                      Setting Password...
                    </div>
                  ) : (
                    'Set Password'
                  )}
                </button>
              </form>
            ) : (
              renderInvalid()
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
